import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, Globe, Lock, Loader2, ExternalLink, CheckCircle2, XCircle, Building2, Package, Wrench, User, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ENTITY_TYPE_LABELS, ROLE_LABELS } from "../types";
import type {
  RoleKey, WizardState, WizardAction, UrlValidationResult, DuplicateDecision,
  ManualSearchCandidate, ManualSearchClarification,
} from "../types";
import type { Dispatch } from "../hooks/useWizardEntity";
import AuthUtils from "@/providers/firebase/authUtils";
import { ManualEntityResolvePanel } from "./ManualEntityResolvePanel";
import { WAIT_BUDGET_MS } from "@/lib/waitExpectations";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const ROLE_TO_REL: Record<string, [string, string | null]> = {
  founder_ceo:  ["governance_role", "Founder / CEO"],
  co_founder:   ["governance_role", "Co-Founder"],
  employee:     ["employee",         null],
  investor:     ["governance_role", "Investor"],
  advisor:      ["governance_role", "Advisor"],
  partner:      ["governance_role", "Partner"],
  freelancer:   ["employee",         "Freelancer"],
  other:        ["governance_role", null],
};

const ROLE_OPTIONS: { key: RoleKey; label: string }[] = [
  { key: "founder_ceo", label: "Founder / CEO" },
  { key: "co_founder",  label: "Co-Founder" },
  { key: "employee",    label: "Employee" },
  { key: "investor",    label: "Investor" },
  { key: "advisor",     label: "Advisor" },
  { key: "partner",     label: "Partner" },
  { key: "freelancer",  label: "Freelancer" },
  { key: "other",       label: "Other" },
];

interface Step1Props {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  onValidateUrl: (url: string) => void;
  onStartExtraction: (url: string, mode: "company" | "product" | "service" | "person") => Promise<boolean>;
  onDuplicateDecision: (forceCreate: boolean) => void;
  onResumeSession: (sessionId: string, step: number) => void;
  onClose: () => void;
  /** PROJECT-077 — manual-entry AI web-search resolver. */
  onSearchManual: (description: string, clarifications: ManualSearchClarification[]) => Promise<void>;
  onResolveManual: (candidate: ManualSearchCandidate | null, description: string) => Promise<boolean>;
}

function ExistingEntityBanner({
  validation,
  onNavigateToEntity,
  onAddDifferent,
}: {
  validation: UrlValidationResult;
  onNavigateToEntity: () => void;
  onAddDifferent: () => void;
}) {
  const [checking, setChecking] = useState(true);
  const [myRel, setMyRel] = useState<{ type: string; title: string | null } | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleKey | null>(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    async function check() {
      setChecking(true);
      try {
        const userEntityId = AuthUtils.getStoredUser()?.entity_id;
        if (!userEntityId || !validation.entity_id) { setChecking(false); return; }
        const headers = await AuthUtils.getAuthHeaders();
        const relRes = await fetch(`${API_URL}/api/v1/entity/relationship/list`, {
          method: "POST", headers,
          body: JSON.stringify({ entity_id: userEntityId }),
        });
        if (relRes.ok) {
          const relJson = await relRes.json();
          const rels: any[] = relJson.data?.relationships ?? [];
          const existing = rels.find((r) => r.related_entity_id === validation.entity_id && r.is_active);
          if (existing) setMyRel({ type: existing.relationship_type, title: existing.role_title ?? null });
        }
      } catch { /* ignore */ }
      finally { setChecking(false); }
    }
    check();
  }, [validation.entity_id]);

  const handleJoin = async () => {
    if (!selectedRole || !validation.entity_id) return;
    const userEntityId = AuthUtils.getStoredUser()?.entity_id;
    if (!userEntityId) return;
    setJoining(true);
    try {
      const headers = await AuthUtils.getAuthHeaders();
      const [relType, roleTitle] = ROLE_TO_REL[selectedRole] ?? ["governance_role", null];
      await fetch(`${API_URL}/api/v1/entity/relationship/add`, {
        method: "POST", headers,
        body: JSON.stringify({
          entity_id: userEntityId,
          related_entity_id: validation.entity_id,
          relationship_type: relType,
          role_title: roleTitle,
        }),
      });
      setJoined(true);
      setTimeout(onNavigateToEntity, 800);
    } catch { /* ignore */ }
    finally { setJoining(false); }
  };

  const typeLabel = ENTITY_TYPE_LABELS[validation.entity_type ?? ""] ?? "Entity";

  return (
    <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.05] p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-foreground">{validation.entity_name}</p>
          <p className="text-md text-muted-foreground/70 mt-0.5">{typeLabel} · Already in workspace</p>
        </div>
        <div className="h-8 w-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
          <Globe className="h-4 w-4 text-blue-400" />
        </div>
      </div>

      {checking ? (
        <div className="flex items-center gap-2 text-md text-muted-foreground/70">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking your role…
        </div>
      ) : joined ? (
        <div className="flex items-center gap-2 text-lg text-green-400">
          <CheckCircle2 className="h-4 w-4" />
          Role added — taking you there…
        </div>
      ) : myRel ? (
        <>
          <div className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
            <span className="text-foreground">
              You're{" "}
              <Badge variant="secondary" className="text-md mx-0.5">
                {myRel.title ?? myRel.type.replace("_", " ")}
              </Badge>{" "}
              here
            </span>
          </div>
          <div className="flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  className="h-8 text-md bg-blue-600 hover:bg-blue-500 text-white gap-1.5"
                  onClick={onNavigateToEntity}
                >
                  <ExternalLink className="h-3 w-3" />
                  View entity page
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-left">
                <p className="text-sm leading-relaxed">Open the existing entity page instead of creating a new one.</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 text-md border-border/60" onClick={onAddDifferent}>
                  Add different
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-left">
                <p className="text-sm leading-relaxed">Clear the URL and add a different entity.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <p className="text-md text-muted-foreground/70">
            You don't have a role here yet — pick one to join, or just view the page.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ROLE_OPTIONS.map(({ key, label }) => (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSelectedRole(key === selectedRole ? null : key)}
                    className={cn(
                      "px-2.5 py-1 rounded-full text-md border transition-all",
                      selectedRole === key
                        ? "border-blue-500 bg-blue-500/20 text-blue-400 font-medium"
                        : "border-border/60 text-muted-foreground hover:border-blue-400/50 hover:text-foreground"
                    )}
                  >
                    {label}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-left">
                  <p className="text-sm leading-relaxed">Select this role to create a relationship with the existing entity.</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  className="h-8 text-md bg-blue-600 hover:bg-blue-500 text-white gap-1.5"
                  disabled={!selectedRole || joining}
                  onClick={handleJoin}
                >
                  {joining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                  {selectedRole ? `Join as ${ROLE_LABELS[selectedRole]}` : "Select a role to join"}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-left">
                <p className="text-sm leading-relaxed">Add yourself to this entity with the selected role.</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5 h-8 text-md border-border/60" onClick={onNavigateToEntity}>
                  <ExternalLink className="h-3 w-3" />
                  Just view page
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-left">
                <p className="text-sm leading-relaxed">Open the entity page without adding yourself.</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" className="text-muted-foreground/70 h-8 text-md" onClick={onAddDifferent}>
                  Add different
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-left">
                <p className="text-sm leading-relaxed">Clear the URL and add a different entity.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </>
      )}
    </div>
  );
}

function DuplicatePrompt({
  decision,
  onDecide,
}: {
  decision: DuplicateDecision;
  onDecide: (forceCreate: boolean) => void;
}) {
  const typeLabel = ENTITY_TYPE_LABELS[decision.existingEntityType] ?? "Entity";
  const pct = Math.round((decision.similarity ?? 0) * 100);
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-foreground">{decision.existingEntityName}</p>
          <p className="text-md text-muted-foreground/70 mt-0.5">
            {typeLabel} · {pct}% match · Already in workspace
          </p>
        </div>
        <div className="h-8 w-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
        </div>
      </div>
      <p className="text-md text-muted-foreground/70">
        This looks similar to an entity that already exists. Is it the same one?
      </p>
      <div className="flex gap-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              className="h-9 text-md bg-amber-500 hover:bg-amber-400 text-white flex-1"
              onClick={() => onDecide(false)}
            >
              Yes, same entity
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-left">
            <p className="text-sm leading-relaxed">Use the existing entity and continue.</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="h-9 text-md border-border/60 flex-1"
              onClick={() => onDecide(true)}
            >
              No, create new
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-left">
            <p className="text-sm leading-relaxed">Create a separate entity anyway.</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

export function Step1Extract({
  state, dispatch, onValidateUrl, onStartExtraction, onDuplicateDecision, onResumeSession, onClose,
  onSearchManual, onResolveManual,
}: Step1Props) {
  const navigate = useNavigate();
  const [localUrl, setLocalUrl] = useState(state.url);
  const [showManual, setShowManual] = useState(false);
  const [manualDesc, setManualDesc] = useState("");
  // onResolveManual has no other loading signal of its own (unlike the URL path's
  // state.isExtracting) — without this the Continue button gave zero feedback while
  // /entity/create was in flight, reading as "stuck" for however long that request took.
  const [isCreatingManual, setIsCreatingManual] = useState(false);
  const entityTypeHint = state.entityTypeHint;
  const inputRef = useRef<HTMLInputElement>(null);
  const manualTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleChange = (val: string) => {
    setLocalUrl(val);
    dispatch({ type: "SET_URL", url: val });
    onValidateUrl(val);
  };

  const validation: UrlValidationResult | null = state.urlValidation;

  // Must look like a real URL: has a dot and at least one TLD-like segment
  // Accepts: "example.com", "https://example.com", "sub.example.co.uk"
  // Rejects: "sadfds", "hello", "test"
  const isUrlLike = /^(https?:\/\/)?[\w-]+(\.[\w-]{2,})+/.test(localUrl.trim());

  const duplicatePending = state.duplicateDecision?.status === "pending";

  const canContinue = showManual
    ? (state.manualSearchSelectedIndex !== null || state.manualEscapeHatch) &&
      !duplicatePending && state.manualSearchStatus !== "searching" && !isCreatingManual
    : isUrlLike &&
      !state.isExtracting &&
      !state.isValidatingUrl &&
      !duplicatePending &&
      validation?.status !== "existing_entity" &&
      validation?.status !== "unreachable";

  const handleContinue = async () => {
    if (showManual) {
      // PROJECT-077 — resolve via AI web search instead of taking manualDesc at
      // face value. Escape hatch (no selection, escape hatch set) passes candidate
      // null; onResolveManual still creates the entity from the raw description,
      // which is what actually fixes the old dead end (entity/create used to never
      // fire on this path at all).
      const selectedCandidate = state.manualEscapeHatch || state.manualSearchSelectedIndex === null
        ? null
        : state.manualSearchCandidates[state.manualSearchSelectedIndex];
      setIsCreatingManual(true);
      try {
        const proceeded = await onResolveManual(selectedCandidate, manualDesc);
        if (proceeded) {
          dispatch({ type: "SET_STEP", step: state.step + 1, direction: 1 });
        }
      } finally {
        setIsCreatingManual(false);
      }
      return;
    }
    if (!localUrl.trim()) return;
    // Start extraction (runs in parallel, status shown in header) and advance to the
    // first question page immediately — the scan continues while the user answers.
    // If a fuzzy duplicate is found, onStartExtraction returns false and Step 1 stays
    // open until the user confirms in the DuplicatePrompt.
    const proceeded = await onStartExtraction(localUrl, entityTypeHint);
    if (proceeded) {
      dispatch({ type: "SET_STEP", step: state.step + 1, direction: 1 });
    }
  };

  const handleBack = () => {
    dispatch({ type: "SET_STEP", step: state.step - 1, direction: -1 });
  };

  const handleNavigateToEntity = () => {
    onClose();
    navigate("/ecosystem");
  };

  const handleAddDifferent = () => {
    dispatch({ type: "SET_URL_VALIDATION", result: null });
    dispatch({ type: "SET_URL", url: "" });
    setLocalUrl("");
  };

  const showValidIndicator = !state.isValidatingUrl && validation?.status === "new" && isUrlLike;
  const showUnreachable   = !state.isValidatingUrl && validation?.status === "unreachable" && isUrlLike;

  return (
    <div className="max-w-2xl mx-auto px-6 py-14 flex flex-col gap-8">
      {/* Hero */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground tracking-tight">
          What entity are you working on?
        </h1>
        <p className="text-lg text-muted-foreground/70 mt-2 leading-relaxed">
          Paste the entity URL — Raisy reads it and extracts everything automatically.
        </p>
        <p className="text-sm text-subtle-foreground mt-1 leading-relaxed">
          The scan runs in the background while you answer the next questions — quick setup is done in seconds;
          the full scan can take up to ~{Math.round(WAIT_BUDGET_MS.wizardScan / 1000)}s on a page that lists a lot.
        </p>
      </div>

      {/* Input + validation area */}
      <div className="flex flex-col gap-3">

        {/* Entity type selector — drives Step 2 role options immediately */}
        <div className="flex items-center justify-between">
          <span className="text-md text-muted-foreground/70 flex items-center gap-1.5">
            What are you adding?
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex items-center justify-center rounded-full hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50">
                  <Info className="h-3.5 w-3.5 text-muted-foreground/70" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-left">
                <p className="font-medium mb-1">Why this matters</p>
                <p className="text-sm leading-relaxed opacity-90">Raisy shows role options that match the entity type. You can change this later if the scraper detects something different.</p>
              </TooltipContent>
            </Tooltip>
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {([
            { key: "company", label: "Company", icon: Building2, tooltip: "A business, organisation, or governing body." },
            { key: "product", label: "Product", icon: Package, tooltip: "A product, app, or tangible offering." },
            { key: "service", label: "Service", icon: Wrench, tooltip: "A service, consultancy, or labour-based offering." },
            { key: "person",  label: "Person",  icon: User,    tooltip: "An individual, team member, or contact." },
          ] as const).map(({ key, label, icon: Icon, tooltip }) => (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => dispatch({ type: "SET_ENTITY_TYPE_HINT", value: key })}
                  className={cn(
                    "flex flex-col items-center gap-1.5 rounded-xl border p-2.5 text-center transition-all relative",
                    state.entityTypeHint === key
                      ? "border-blue-500/60 bg-blue-500/10 text-blue-400"
                      : "border-border/40 text-muted-foreground/70 hover:border-border/80 hover:text-foreground bg-muted/10"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-label font-medium leading-tight">{label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-left">
                <p className="font-medium mb-0.5">{label}</p>
                <p className="text-sm leading-relaxed opacity-90">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

        {/* Public / Private visibility toggle */}
        <div className="flex items-center justify-between">
          <span className="text-md text-muted-foreground/70 flex items-center gap-1.5">
            Visibility
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex items-center justify-center rounded-full hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50">
                  <Info className="h-3.5 w-3.5 text-muted-foreground/70" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-left">
                <p className="font-medium mb-1">Who can discover this entity?</p>
                <p className="text-sm leading-relaxed opacity-90">
                  <span className="text-blue-300 font-medium">Public</span> — anyone on Raisy can find it in search, lists, and ecosystem maps.
                </p>
                <p className="text-sm leading-relaxed opacity-90 mt-1">
                  <span className="text-amber-300 font-medium">Private</span> — hidden from public discovery; only you and invited team members can see it.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
        </div>
        <div className="flex gap-2">
          {(["public", "private"] as const).map((v) => (
            <Tooltip key={v}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => dispatch({ type: "SET_VISIBILITY", value: v })}
                  className={cn(
                    "flex-1 h-9 rounded-lg border text-md font-medium transition-all duration-150 flex items-center justify-center gap-1.5",
                    state.visibility === v
                      ? v === "private"
                        ? "border-amber-500/60 bg-amber-500/10 text-amber-400"
                        : "border-blue-500 bg-blue-500/10 text-blue-400"
                      : "border-border/50 text-muted-foreground/70 hover:border-border hover:text-foreground bg-muted/10"
                  )}
                >
                  {v === "private" ? (
                    <><Lock className="h-3 w-3" /> Private</>
                  ) : (
                    <><Globe className="h-3 w-3" /> Public</>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-left">
                {v === "public" ? (
                  <>
                    <p className="font-medium mb-0.5">Public entity</p>
                    <p className="text-sm leading-relaxed opacity-90">Any Raisy user can discover this entity in search, lists, and ecosystem maps.</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium mb-0.5">Private entity</p>
                    <p className="text-sm leading-relaxed opacity-90">Hidden from public discovery. Only you and people you grant access can see it.</p>
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

        {/* URL input — hidden when manual mode is active */}
        {!showManual && (
          <>
            <div className="relative">
              <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle-foreground z-10 pointer-events-none" />
              <Input
                ref={inputRef}
                className={cn(
                  "pl-10 h-11 text-lg rounded-lg transition-all duration-150",
                  "bg-muted/20 border-border/50 focus-visible:border-blue-500/50 focus-visible:ring-blue-500/20",
                  "placeholder:text-subtle-foreground",
                  (showValidIndicator || showUnreachable) ? "pr-36" : "",
                  showUnreachable ? "border-red-500/40 focus-visible:border-red-500/50 focus-visible:ring-red-500/20" : ""
                )}
                placeholder="https://yourcompany.com"
                value={localUrl}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canContinue && handleContinue()}
                disabled={state.isExtracting}
              />
              {state.isValidatingUrl ? (
                <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-subtle-foreground" />
              ) : showValidIndicator ? (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-400">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-md font-medium">Valid URL</span>
                </div>
              ) : showUnreachable ? (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1 text-red-400">
                  <XCircle className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-md font-medium">Can't reach site</span>
                </div>
              ) : null}
            </div>

            {/* QUICK SETUP (wizard step 1). Sits with the URL because it only means anything
                when there is a page to read — the manual-entry path never scrapes, so this
                rides the same {!showManual} fence as the input above it. Sent as
                `quick_scrape` on the extraction socket (useWizardEntity.openExtractionSocket). */}
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border/40 bg-muted/10 px-3.5 py-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-md text-foreground/80 flex items-center gap-1.5">
                  Quick setup
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex items-center justify-center rounded-full hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50">
                        <Info className="h-3.5 w-3.5 text-muted-foreground/70" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[280px] text-left">
                      <p className="font-medium mb-1">Quick setup vs. full scan</p>
                      <p className="text-sm leading-relaxed opacity-90">
                        Both read this entity's own details the same way — name, description, type, industry, location.
                      </p>
                      <p className="text-sm leading-relaxed opacity-90 mt-1">
                        The full scan goes further: it collects the products, brands and stakeholders the page
                        mentions and offers them to you for review. That discovery is what can take up to 60 seconds
                        on a page that lists a lot, and it uses more credits — the charge is per token.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </span>
                <span className="text-sm text-subtle-foreground leading-relaxed">
                  {state.quickScrape
                    ? "Reads the main entity only — quickest, and the lightest on credits."
                    : "Collects every entity, product and stakeholder it can find — this can take up to 60 seconds."}
                </span>
              </div>
              <Switch
                checked={state.quickScrape}
                onCheckedChange={(value) => dispatch({ type: "SET_QUICK_SCRAPE", value })}
                aria-label="Quick setup — read this entity only"
                className="mt-1 shrink-0"
              />
            </div>

            {validation?.status === "existing_entity" && (
              <ExistingEntityBanner
                validation={validation}
                onNavigateToEntity={handleNavigateToEntity}
                onAddDifferent={handleAddDifferent}
              />
            )}

            {duplicatePending && state.duplicateDecision && (
              <DuplicatePrompt
                decision={state.duplicateDecision}
                onDecide={onDuplicateDecision}
              />
            )}

            {validation?.status === "in_progress_session" && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4 flex flex-col gap-3">
                <div>
                  <p className="text-lg font-semibold text-amber-400">Session in progress</p>
                  <p className="text-md text-muted-foreground/70 mt-0.5">
                    You started the wizard for this URL at step {validation.current_step}.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-8 text-md bg-amber-600/80 hover:bg-amber-600 text-white border-0"
                    onClick={() => onResumeSession(validation.session_id!, validation.current_step!)}
                  >
                    Resume where I left off
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-md text-muted-foreground/70 hover:text-foreground"
                    onClick={() => dispatch({ type: "SET_URL_VALIDATION", result: null })}
                  >
                    Start fresh
                  </Button>
                </div>
              </div>
            )}

            {state.error && (
              <Alert variant="destructive" className="py-3 rounded-xl border-destructive/30 bg-destructive/5">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-lg">{state.error}</AlertDescription>
              </Alert>
            )}
          </>
        )}

        {/* Manual entry panel — PROJECT-077: AI web-search resolver replaces raw pass-through */}
        {showManual && (
          <div className="flex flex-col gap-3">
            <textarea
              ref={manualTextareaRef}
              autoFocus
              value={manualDesc}
              onChange={(e) => {
                setManualDesc(e.target.value);
                if (state.manualSearchStatus !== "idle") {
                  dispatch({ type: "RESET_MANUAL_SEARCH" });
                }
              }}
              className={cn(
                "w-full rounded-lg border border-border/50 bg-muted/20 px-3.5 py-2.5",
                "text-lg resize-none text-foreground placeholder:text-subtle-foreground",
                "focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/40",
                "transition-all duration-150"
              )}
              rows={4}
              placeholder="e.g. Jane Smith, product designer at Acme — or: Acme Corp, a B2B SaaS platform for marketing teams…"
            />

            {state.manualSearchStatus === "idle" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 text-md border-border/60 self-start gap-1.5"
                    disabled={!manualDesc.trim()}
                    onClick={() => onSearchManual(manualDesc, [])}
                  >
                    <Globe className="h-3.5 w-3.5" />
                    Find matches
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-left">
                  <p className="text-sm leading-relaxed">Search the web to find the real person or company you mean, instead of just using this text as-is.</p>
                </TooltipContent>
              </Tooltip>
            )}

            <ManualEntityResolvePanel
              status={state.manualSearchStatus}
              candidates={state.manualSearchCandidates}
              selectedIndex={state.manualSearchSelectedIndex}
              onSelect={(index) => dispatch({ type: "SELECT_MANUAL_CANDIDATE", index })}
              escapeHatch={state.manualEscapeHatch}
              onEscapeHatch={() => dispatch({ type: "SET_MANUAL_ESCAPE_HATCH", value: !state.manualEscapeHatch })}
              questions={state.manualSearchQuestions}
              onAnswerClarification={(answer) => {
                const question = state.manualSearchQuestions[0] ?? "";
                dispatch({ type: "ADD_MANUAL_CLARIFICATION", question, answer });
                onSearchManual(manualDesc, [...state.manualSearchClarifications, { question, answer }]);
              }}
              error={state.manualSearchError}
              onEditDescription={() => {
                dispatch({ type: "RESET_MANUAL_SEARCH" });
                setTimeout(() => manualTextareaRef.current?.focus(), 50);
              }}
              onSearchMore={() => {
                const question = "None of the suggested matches were correct";
                const answer = "Search again and suggest other possible matches, different from the ones already shown.";
                dispatch({ type: "ADD_MANUAL_CLARIFICATION", question, answer });
                onSearchManual(manualDesc, [...state.manualSearchClarifications, { question, answer }]);
              }}
            />
          </div>
        )}

        {/* "No website?" toggle — above Continue */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="flex items-center gap-1.5 text-md text-subtle-foreground hover:text-muted-foreground transition-colors self-start"
              onClick={() => {
                setShowManual(!showManual);
                if (showManual) {
                  // Switching back to URL mode — restore URL input focus
                  setTimeout(() => inputRef.current?.focus(), 50);
                }
              }}
            >
              <span className="text-label">{showManual ? "▲" : "▼"}</span>
              {showManual ? "Back to URL entry" : "No website? Describe it manually"}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-left">
            <p className="text-sm leading-relaxed">
              {showManual
                ? "Switch back to entering a website URL for automatic extraction."
                : "Skip the website scan and describe the entity in your own words."}
            </p>
          </TooltipContent>
        </Tooltip>

        <div className="flex items-center justify-between gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-lg text-muted-foreground/70 hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-left">
              <p className="text-sm leading-relaxed">Change whether this is your own entity or an external one. Anything entered here is saved.</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleContinue}
                disabled={!canContinue}
                className={cn(
                  "flex-1 h-11 gap-2 text-lg font-medium rounded-lg transition-all duration-200",
                  "bg-blue-600 hover:bg-blue-500 text-white",
                  canContinue
                    ? "hover:shadow-lg hover:shadow-blue-600/20 active:scale-[0.99]"
                    : "opacity-40 cursor-not-allowed"
                )}
              >
                {isCreatingManual ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px] text-left">
              <p className="text-sm leading-relaxed">
                {showManual
                  ? "Create the entity from your manual description and continue."
                  : "Start scanning the website and continue to the next question."}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
